import React, { useState } from 'react';
import { Bot, CalendarDays, LockKeyhole, Unplug } from 'lucide-react';
import { api } from '../lib/api.js';
import { Card } from '../components/ui.jsx';

export function SettingsPage({
  me,
  outlookConfigured,
  outlookConnected,
  outlookNeedsReconnect,
  outlookAccountEmail,
  mcpConfigured,
  mcpConnections = [],
  reload,
}) {
  const [newPassword, setNew] = useState('');
  const [confirmPassword, setConfirm] = useState('');
  const [message, setMessage] = useState('');
  const [outlookMessage, setOutlookMessage] = useState('');
  const [disconnecting, setDisconnecting] = useState(false);
  const [mcpMessage, setMcpMessage] = useState('');
  const [revokingClientId, setRevokingClientId] = useState('');

  async function revokeMcpConnection(clientId) {
    setRevokingClientId(clientId);
    setMcpMessage('');
    try {
      await api(`/mcp/connections/${clientId}`, { method: 'DELETE' });
      await reload();
    } catch (err) {
      setMcpMessage(err.message);
    } finally {
      setRevokingClientId('');
    }
  }

  function connectOutlook() {
    window.location.assign(
      new URL('api/outlook/connect', window.location.href),
    );
  }

  async function disconnectOutlook() {
    setDisconnecting(true);
    setOutlookMessage('');
    try {
      await api('/outlook', { method: 'DELETE' });
      await reload();
      setOutlookMessage('Outlook calendar disconnected.');
    } catch (err) {
      setOutlookMessage(err.message);
    } finally {
      setDisconnecting(false);
    }
  }

  async function submit(event) {
    event.preventDefault();
    setMessage('');
    if (newPassword !== confirmPassword) {
      setMessage('Passwords do not match.');
      return;
    }
    try {
      await api('/me/password', {
        method: 'POST',
        body: JSON.stringify({ newPassword, confirmPassword }),
      });
      setNew('');
      setConfirm('');
      setMessage('Password changed.');
    } catch (err) {
      setMessage(err.message);
    }
  }

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>Settings</h1>
          <p>{me.email}</p>
        </div>
      </div>
      <Card title="Change password">
        <form className="form-grid narrow" onSubmit={submit}>
          <label>
            New password
            <input
              type="password"
              minLength={12}
              value={newPassword}
              onChange={(event) => setNew(event.target.value)}
            />
          </label>
          <label>
            Confirm password
            <input
              type="password"
              minLength={12}
              value={confirmPassword}
              onChange={(event) => setConfirm(event.target.value)}
            />
          </label>
          <button className="secondary">
            <LockKeyhole size={14} /> Change password
          </button>
          {message && <span className="hint">{message}</span>}
        </form>
      </Card>
      {(outlookConfigured || outlookConnected || outlookNeedsReconnect) && (
        <Card title="Outlook Calendar">
          <div className="outlook-settings">
            <div className="outlook-settings-copy">
              <CalendarDays size={20} />
              <div>
                {!outlookConfigured ? (
                  <>
                    <strong>Outlook Calendar is unavailable</strong>
                    <p>
                      Outlook Calendar has not been configured for this
                      installation.
                    </p>
                  </>
                ) : outlookConnected ? (
                  <>
                    <strong>Connected to {outlookAccountEmail}</strong>
                    <p>
                      Your events are fetched live, visible only to you, and are
                      never stored here.
                    </p>
                  </>
                ) : outlookNeedsReconnect ? (
                  <>
                    <strong>Outlook needs to be reconnected</strong>
                    <p>
                      Your calendar access expired or was revoked. Reconnect to
                      show events again.
                    </p>
                  </>
                ) : (
                  <>
                    <strong>Not connected</strong>
                    <p>
                      Show your Outlook calendar as a private, read-only
                      planning overlay.
                    </p>
                  </>
                )}
              </div>
            </div>
            {outlookConnected || outlookNeedsReconnect ? (
              <div className="outlook-settings-actions">
                {outlookConfigured && outlookNeedsReconnect && (
                  <button
                    type="button"
                    className="secondary"
                    onClick={connectOutlook}
                  >
                    <CalendarDays size={14} /> Reconnect Outlook
                  </button>
                )}
                <button
                  type="button"
                  className="danger"
                  disabled={disconnecting}
                  onClick={disconnectOutlook}
                >
                  <Unplug size={14} />{' '}
                  {disconnecting ? 'Disconnecting...' : 'Disconnect'}
                </button>
              </div>
            ) : outlookConfigured ? (
              <button
                type="button"
                className="secondary"
                onClick={connectOutlook}
              >
                <CalendarDays size={14} /> Connect Outlook
              </button>
            ) : null}
          </div>
          {outlookMessage && <p className="hint">{outlookMessage}</p>}
        </Card>
      )}
      {(mcpConfigured || mcpConnections.length > 0) && (
        <Card title="Connected AI clients">
          {mcpConnections.length === 0 ? (
            <div className="outlook-settings-copy">
              <Bot size={20} />
              <div>
                <strong>No AI clients connected</strong>
                <p>
                  Connect Claude or another MCP client to Time Tracker from that
                  client&rsquo;s own settings — once you approve it, it acts as
                  you, and every change it makes is recorded against your name.
                </p>
              </div>
            </div>
          ) : (
            mcpConnections.map((connection) => (
              <div className="outlook-settings" key={connection.clientId}>
                <div className="outlook-settings-copy">
                  <Bot size={20} />
                  <div>
                    <strong>{connection.clientName}</strong>
                    <p>
                      Connected {formatDate(connection.connectedAt)}
                      {connection.lastUsedAt &&
                        ` · last used ${formatDate(connection.lastUsedAt)}`}
                    </p>
                  </div>
                </div>
                <div className="outlook-settings-actions">
                  <button
                    type="button"
                    className="danger"
                    disabled={revokingClientId === connection.clientId}
                    onClick={() => revokeMcpConnection(connection.clientId)}
                  >
                    <Unplug size={14} />{' '}
                    {revokingClientId === connection.clientId
                      ? 'Disconnecting...'
                      : 'Disconnect'}
                  </button>
                </div>
              </div>
            ))
          )}
          {mcpMessage && <p className="hint">{mcpMessage}</p>}
        </Card>
      )}
    </div>
  );
}

function formatDate(value) {
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
